import { useState, useMemo } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { toggleSidebarCollapsed } from '../features/ui/uiSlice';
import { useLogoutMutation } from '../features/auth/authApi';
import { logOut as authLogOut, selectViewMode } from '../features/auth/authSlice';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

interface SidebarProps {
    className?: string;
}

type NavItem = {
    name: string;
    path: string;
    icon: JSX.Element;
    roles?: string[]; // Optional roles that can see this item
};

type NavSection = {
    title: string;
    id: string;
    items: NavItem[];
    roles?: string[]; // Optional roles that can see this section
};

const Sidebar = ({ className = '' }: SidebarProps) => {
    const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
    const isSidebarCollapsed = useAppSelector((state) => state.ui.isSidebarCollapsed);
    const user = useAppSelector((state) => state.auth.user);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const viewMode = useAppSelector(selectViewMode);
    const [logoutApi] = useLogoutMutation();

    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        'core': true,
        'talent': true,
        'payroll': true,
        'admin': true,
        'documents': true,
        'ess': true
    });

    const toggleSection = (id: string) => {
        if (isSidebarCollapsed) return; // Don't toggle sections when collapsed
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleLogout = async () => {
        try {
            await logoutApi().unwrap();
            dispatch(authLogOut());
            navigate('/login');
        } catch (error) {
            console.error('Logout failed', error);
            dispatch(authLogOut());
            navigate('/login');
        }
    };

    const userRole = user?.role?.toLowerCase() || '';
    const isAdmin = ['admin', 'super_admin', 'hr_admin'].includes(userRole);
    const isHR = ['hr', 'hr_admin'].includes(userRole);
    const isManager = userRole === 'manager';

    const navigation: NavSection[] = useMemo(() => {
        const fullNavigation: NavSection[] = [
            {
                title: 'Core HR',
                id: 'core',
                items: [
                    {
                        name: 'Dashboard', path: '/dashboard', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                        )
                    },
                    {
                        name: 'Departments', path: '/departments', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        )
                    },
                    {
                        name: 'Employees', path: '/employees', roles: ['admin', 'super_admin', 'hr_admin', 'hr', 'manager'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        )
                    },
                    {
                        name: 'Add Employee', path: '/employees/create', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                        )
                    },
                    {
                        name: 'Rostering', path: '/rostering', roles: ['admin', 'super_admin', 'hr_admin', 'hr', 'manager'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                        )
                    },
                    {
                        name: 'Leaves', path: '/leaves', roles: ['admin', 'super_admin', 'hr_admin', 'hr', 'manager'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        )
                    },
                    {
                        name: 'Compliance', path: '/compliance', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        )
                    },
                    {
                        name: 'Branches', path: '/branches', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        )
                    },
                ]
            },
            {
                title: 'Talent Management',
                id: 'talent',
                roles: ['admin', 'super_admin', 'hr_admin', 'hr', 'manager'],
                items: [
                    {
                        name: 'Hiring', path: '/hiring', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 00-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'Training', path: '/training', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                        )
                    },
                    {
                        name: 'Performance', path: '/performance', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                        )
                    },
                ]
            },
            {
                title: 'Payroll & Finance',
                id: 'payroll',
                roles: ['admin', 'super_admin', 'hr_admin', 'hr'],
                items: [
                    {
                        name: 'Payroll Hub', path: '/payroll/dashboard', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                        )
                    },
                    {
                        name: 'Payroll History', path: '/payroll', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        )
                    },
                    {
                        name: 'Salary History', path: '/payroll/history', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'Salary Revision & Arrears', path: '/increments', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                        )
                    },
                    {
                        name: 'Adjustments & Loans', path: '/loans', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm3-2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        )
                    },
                ]
            },
            {
                title: 'Document Management System',
                id: 'documents',
                roles: ['admin', 'super_admin', 'hr_admin', 'hr'],
                items: [
                    {
                        name: 'Company Documents', path: '/settings/documents', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                        )
                    },
                    {
                        name: 'Template Library', path: '/documents/templates', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'Generated Documents', path: '/documents/generated', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                ]
            },
            {
                title: 'Admin & Settings',
                id: 'admin',
                roles: ['admin', 'super_admin', 'hr_admin', 'hr'],
                // Ordered general -> structural -> access -> process -> module
                // admin -> reporting/data, so the section reads as a narrowing
                // scope rather than an arbitrary list. Global Settings leads
                // because it's the entry point people reach for most often;
                // Data Management sits last as the heaviest, least-frequent and
                // most destructive tool in the group.
                items: [
                    // ── Configuration ──
                    {
                        name: 'Global Settings', path: '/settings/global', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        )
                    },
                    {
                        name: 'Org Structure', path: '/settings/org', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        )
                    },
                    {
                        name: 'Security', path: '/settings/security', roles: ['admin', 'super_admin', 'hr_admin'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        )
                    },

                    // ── Day-to-day administration ──
                    {
                        name: 'Approvals', path: '/requests/approvals', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        )
                    },
                    {
                        name: 'Attendance', path: '/attendance', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        )
                    },
                    {
                        name: 'Assets', path: '/assets', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                        )
                    },
                    {
                        name: 'Benefits', path: '/benefits', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        )
                    },

                    // ── Reporting & data ──
                    {
                        name: 'Reports', path: '/reports', roles: ['admin', 'super_admin', 'hr_admin', 'hr'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'Data Management', path: '/settings/data-management', roles: ['admin', 'super_admin', 'hr_admin'], icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7m-8-2V3m0 0l-3 3m3-3l3 3m-9 5h12" /></svg>
                        )
                    },
                ]
            },
            {
                title: 'Self Service',
                id: 'ess',
                items: [
                    {
                        name: 'My Dashboard', path: '/ess/dashboard', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                        )
                    },
                    {
                        name: 'Request Center', path: '/ess/requests', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'My Profile', path: '/ess/profile', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        )
                    },
                    {
                        name: 'Documents', path: '/ess/documents', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        )
                    },
                    {
                        name: 'Salary Advance', path: '/ess/advance-request', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        )
                    },
                    {
                        name: 'Calendar', path: '/ess/calendar', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        )
                    },
                    {
                        name: 'Apply Leave', path: '/ess/leave/apply', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        )
                    },
                    {
                        name: 'My Assets', path: '/ess/assets', icon: (
                            <svg className="w-5 h-5 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                        )
                    },
                ]
            }
        ];

        // Filter based on roles AND viewMode.
        //
        // IMPORTANT: `user.role` is a coarse, collapsed 'admin' | 'employee'
        // label (see authService.ts) - it can't distinguish a true built-in
        // power role (SUPER_ADMIN/ADMIN/HR_ADMIN) from a custom or
        // lower-privilege role (MANAGER, SUPERVISOR, or any role created in
        // Security Center) that merely has clearance_level <= 5. Using it
        // here made every `roles: [...]` allowlist below pass unconditionally
        // for ANY admin-viewMode user, which meant a role with only a couple
        // of permission boxes ticked in Security Center still saw the entire
        // admin sidebar - the granular checkboxes had no effect on what was
        // shown, only on whether the resulting click failed with a 403.
        // `user.roles` (plural) is the REAL array of role codes the backend
        // already sends - use that instead so role allowlists and the
        // permission checks below actually gate visibility per role.
        const realRoles = (user?.roles || []).map(r => r.toLowerCase());
        const isPowerRole = realRoles.some(r => ['super_admin', 'admin', 'hr_admin'].includes(r));
        const userPermissions = user?.permissions || [];

        const matchesRoles = (allowed?: string[]) => {
            if (isPowerRole) return true;
            if (!allowed || allowed.length === 0) return true;
            const normalized = allowed.map(r => r.toLowerCase());
            return realRoles.some(r => normalized.includes(r));
        };
        const matchesPermission = (required?: string | string[] | null) => {
            if (!required) return false;
            const list = Array.isArray(required) ? required : [required];
            return list.some(p => userPermissions.includes(p));
        };

        // One consolidated permission per whole section (Payroll & Finance,
        // Talent Management) - having ANY of these grants the entire
        // section, rather than needing per-item permissions inside it.
        const sectionPermission: Partial<Record<string, string | string[]>> = {
            payroll: ['payroll:view', 'payroll:process'],
            talent: 'talent:view',
        };

        // Per-item permission fallback - lets a role that was granted just
        // this one permission in Security Center see the matching item even
        // when it doesn't hold any of the item's named roles.
        const itemPermission: Partial<Record<string, string | string[]>> = {
            '/requests/approvals': ['approvals:view', 'approvals:manage', 'approvals:process'],
            '/employees': ['employee:view', 'employees:view'],
            '/employees/create': ['employee:edit', 'employees:create'],
            '/payroll': 'payroll:view',
            '/rostering': ['rostering:view', 'rostering:manage'],
            '/leaves': ['leaves:view', 'leaves:manage'],
            '/attendance': ['attendance:view', 'attendance:log'],
            '/settings/documents': 'documents:view',
            '/settings/global': ['settings:manage', 'settings:edit'],
        };

        return fullNavigation
            .map(section => {
                const sectionRoleAllowed = matchesRoles(section.roles);
                const sectionPermissionAllowed = matchesPermission(sectionPermission[section.id]);

                const items = section.items.filter(item => {
                    // An item with no `roles` of its own (e.g. Payroll Hub, Hiring)
                    // was only ever meant to inherit its section's role check, not
                    // be unconditionally open - falling back to `true` here would
                    // let it slip through even when the section itself failed.
                    const itemRoleAllowed = item.roles ? matchesRoles(item.roles) : sectionRoleAllowed;
                    const itemPermissionAllowed = matchesPermission(itemPermission[item.path]);
                    // A whole-section permission (e.g. payroll:view) unlocks every item inside it too.
                    return itemRoleAllowed || itemPermissionAllowed || sectionPermissionAllowed;
                });

                // A section with no blanket role/permission grant can still surface
                // if at least one of its items was individually unlocked above.
                const sectionVisible = sectionRoleAllowed || sectionPermissionAllowed || items.length > 0;

                return { ...section, items, sectionVisible };
            })
            .filter(section => {
                if (!section.sectionVisible) return false;
                // View Mode Logic
                if (viewMode === 'ESS') {
                    return section.id === 'ess';
                } else {
                    return section.id !== 'ess';
                }
            })
            .filter(section => section.items.length > 0)
            .map(({ sectionVisible, ...section }) => section);
    }, [user?.roles, user?.permissions, viewMode]);

    return (
        <aside
            className={`fixed inset-y-0 left-0 z-30 bg-white border-r border-gray-100 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } ${isSidebarCollapsed ? 'w-20' : 'w-72'} ${className}`}
        >
            <div className={`flex-shrink-0 flex items-center h-20 border-b border-gray-50 transition-all duration-300 ${isSidebarCollapsed ? 'px-0 justify-center' : 'px-8 gap-3'}`}>
                <div className="h-9 w-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-100 flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                </div>
                {!isSidebarCollapsed && (
                    <span className="text-gray-900 text-xl font-bold tracking-tight whitespace-nowrap overflow-hidden">
                        noor<span className="text-blue-600">HR</span>
                    </span>
                )}
            </div>

            {/* Workspace Switcher */}
            <div className="mt-4">
                <WorkspaceSwitcher theme="light" isCollapsed={isSidebarCollapsed} />
            </div>

            {/* Collapse Toggle Button */}
            <button
                onClick={() => dispatch(toggleSidebarCollapsed())}
                className="hidden lg:flex absolute top-24 -right-3 h-6 w-6 bg-white border border-gray-100 rounded-full items-center justify-center shadow-sm text-gray-400 hover:text-blue-600 transition-all z-40"
            >
                <svg className={`w-4 h-4 transition-transform duration-300 ${isSidebarCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            <nav className="mt-4 px-4 space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent scrollbar-visible-hover">
                {navigation.map((section) => (
                    <div key={section.id} className="py-2">
                        {!isSidebarCollapsed ? (
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="w-full flex items-center justify-between px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em] hover:text-gray-600 transition-colors"
                            >
                                <span>{section.title}</span>
                                <svg
                                    className={`w-3 h-3 transition-transform duration-200 ${openSections[section.id] ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        ) : (
                            <div className="h-px bg-gray-50 mx-2 my-4" />
                        )}

                        <div className={`space-y-1 mt-1 transition-all duration-300 overflow-hidden ${isSidebarCollapsed || openSections[section.id] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            {section.items.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.path}
                                    title={isSidebarCollapsed ? item.name : ''}
                                    className={({ isActive }) =>
                                        `flex items-center rounded-xl transition-all duration-200 group ${isSidebarCollapsed ? 'px-0 justify-center py-3' : 'px-4 py-3'
                                        } ${isActive
                                            ? 'bg-blue-50 text-blue-600 font-semibold shadow-sm'
                                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                        }`
                                    }
                                >
                                    <span className={`transition-transform duration-200 group-hover:scale-110 ${location.pathname === item.path ? 'scale-110' : ''}`}>
                                        {item.icon}
                                    </span>
                                    {!isSidebarCollapsed && (
                                        <div className="flex-1 flex items-center justify-between min-w-0">
                                            <span className="ml-3.5 text-sm whitespace-nowrap overflow-hidden">{item.name}</span>
                                        </div>
                                    )}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Removed Redundant ESS Toggle — handled by Navbar Switcher */}

            {/* User Profile Block */}
            <div className={`mt-auto border-t border-gray-50 p-4 transition-all duration-300 ${isSidebarCollapsed ? 'flex justify-center' : ''}`}>
                <div className={`group flex items-center ${isSidebarCollapsed ? '' : 'gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer'}`}>
                    <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs ring-4 ring-white shadow-sm flex-shrink-0">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </div>
                    {!isSidebarCollapsed && (
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate uppercase tracking-tight">
                                {user?.firstName} {user?.lastName}
                            </p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest truncate">
                                {user?.role?.toLowerCase()}
                            </p>
                        </div>
                    )}
                    {!isSidebarCollapsed && (
                        <button
                            onClick={handleLogout}
                            className="p-1 px-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Logout"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
